import React from "react";
import { Link } from "react-router-dom";

import freezerVanImg from "../../assets/images/FreezerVan/freezer-van-service.webp";

export const freezerVanForRentSections = [
  {
    type: "fvfr-hero",
    title:
      "Freezer Van for Rent in Dubai & Sharjah — Sub-Zero Deep Freeze Vans for Frozen Cargo Delivery Across the UAE | Hire Today",
    paragraphs: [
      "We offer freezer van for rent in Dubai and Sharjah maintaining internal cargo temperatures down to -18°C throughout every delivery route. Businesses moving frozen meat, seafood, ice cream, and packaged frozen goods depend on consistent sub-zero conditions from the moment cargo is loaded — any temperature deviation during transit affects product integrity in ways that cannot be corrected at the delivery point.",
      "Our freezer van rental services are supported by a fleet of 58 deep-freeze vans available for both scheduled operations and same-day requirements across Dubai and Sharjah. Every vehicle is pre-cooled to -18°C and dispatch-confirmed before departure, with our frozen van fleet assigned based on your specific frozen cargo type, route, and delivery timing rather than a fixed hire arrangement.",
      "Contact our team to arrange freezer van rental in Dubai or Sharjah based on your frozen cargo type and delivery schedule.",
    ],
  },

  {
    type: "fvfr-thermal-methods",
    title:
      "How Our Freezer Vans Maintain -18°C Through UAE Urban Delivery Conditions",
    description:
      "Maintaining stable sub-zero temperatures during urban freezer van delivery in the UAE is operationally more demanding than any other cold chain environment. Our fleet manages this exposure through four strict engineering and operational standards across Dubai and Sharjah:",
    methods: [
      {
        label: "Rapid Compressor Recovery",
        description:
          "Every door opening introduces outside air at 45°C+ directly into a -18°C cargo environment — a 60°C temperature differential that creates immediate internal heat spikes. Our systems are engineered specifically for rapid thermal recovery between stops, maintaining sub-zero stability across multi-stop urban routes where standard units lose cooling capacity.",
      },
      {
        label: "Heavy-Duty Fleet Investment",
        description:
          "Standard commercial freezer van units cannot sustain consistent -18°C performance under repeated door openings in UAE summer heat. Our fleet runs exclusively on Thermo King and Thermal Master refrigeration systems — heavy-duty units built for extreme operating conditions because frozen cargo integrity at urban multi-stop scale demands more than standard van refrigeration can deliver under UAE conditions.",
      },
      {
        label: "Mandatory Pre-Cooling Protocols",
        description:
          "Every vehicle reaches a confirmed stable -18°C across the full internal cargo space before loading begins. A van starting at even -15°C will not recover during transit under UAE ambient conditions — surface thawing begins immediately and cannot be reversed once cargo is in motion.",
      },
      {
        label: "Strategic Route Sequencing",
        description:
          "At sub-zero ranges the thermal tolerance margin is significantly narrower than chilled cargo operations. Multi-stop routes across Dubai and Sharjah are sequenced around cumulative door-opening exposure — stop order, unloading time, and route progression are confirmed before departure to prevent temperature creep.",
      },
    ],
  },

  {
    type: "fvfr-fleet-spec",
    title:
      "Freezer Van Fleet Specifications — Deep Freeze Capability & Load Configuration",
    description:
      "Our freezer vans for rent in Dubai and Sharjah are built around refrigeration system performance first — because sub-zero cargo integrity depends on the unit maintaining -18°C under UAE urban delivery conditions, not just on paper specifications. Every freezer van rental deployment is backed by the following confirmed fleet standards:",
    image: freezerVanImg,
    imageAlt:
      "Freezer van parked for sub-zero frozen cargo delivery across Dubai and Sharjah",
    refrigerationRows: [
      { spec: "Temperature Range", detail: "0°C to -18°C" },
      {
        spec: "Refrigeration Units",
        detail: "Thermo King & Thermal Master heavy-duty systems",
      },
      {
        spec: "Pre-Cooling Requirement",
        detail:
          "Mandatory — loading begins only after -18°C is confirmed stable",
      },
      {
        spec: "Temperature Monitoring",
        detail: "Onboard digital logging throughout every route",
      },
    ],
    payloadRows: [
      { spec: "Primary Model", detail: "Toyota Hiace" },
      { spec: "Payload Capacity", detail: "1 ton — 1.5 ton" },
      { spec: "Roof Models", detail: "Standard roof & high roof available" },
      { spec: "Door Configuration", detail: "Rear door & side door access" },
      {
        spec: "GPS Tracking",
        detail: "Fitted as standard across Dubai & Sharjah fleet",
      },
    ],
    note: "All units operating across our Dubai and Sharjah fleet are RTA-compliant and maintained by in-house mechanics at each branch — keeping refrigeration system performance within operational standards on every freezer van for rent deployment.",
  },

  {
    type: "fvfr-cargo-types",
    title:
      "Frozen Cargo Types Handled by Our Freezer Van Fleet Across Dubai & Sharjah",
    description:
      "Our freezer van rental in Dubai and Sharjah covers four primary frozen cargo categories where sub-zero conditions are non-negotiable from loading through to final delivery. Every freezer van for rent deployment is matched to the specific cargo requirement before dispatch is confirmed:",
    cargoTypes: [
      {
        title: "Frozen Meat & Poultry",
        description:
          "Sub-zero handling for frozen meat and poultry is absolute — any surface thawing between cold storage and retail outlet creates both product loss and UAE food safety compliance risk. This is one of the most regulated frozen cargo categories in Dubai and Sharjah distribution networks, where temperature records are part of the delivery requirement.",
      },
      {
        title: "Ice Cream & Frozen Desserts",
        description:
          "Ice cream requires consistent temperatures below -18°C throughout every transit — partial thawing and refreezing permanently damages product texture and structural integrity in ways visible at the point of sale. Multi-stop urban delivery routes across Dubai and Sharjah create the highest cumulative exposure risk for this cargo type.",
      },
      {
        title: "Seafood & Marine Products",
        description:
          "Frozen seafood moves through some of the most time-sensitive cold chain routes in UAE logistics where temperature deviation during transit affects both product quality and import compliance requirements. Our freezer van hire across Dubai and Sharjah covers marine cargo movement where delivery timing and sub-zero consistency are both part of the handover requirement.",
      },
      {
        title: "Packaged Frozen Goods",
        description:
          <>Frozen vegetables, ready-to-eat meals, and processed food products moving through retail distribution networks across Dubai and Sharjah require consistent sub-zero conditions across the full delivery cycle — regardless of stop frequency, route duration, or ambient temperature conditions during transit. For bulk warehouse-scale frozen goods movement beyond van capacity, our <Link to="/freezer-truck-for-rent" className="internal-link">freezer truck for rent</Link> handles the larger volume.</>,
      },
    ],
  },

  {
    type: "fvfr-pricing",
    title: "Freezer Van Rental Rates in Dubai & Sharjah",
    descriptionMain:
      "Freezer van rental rates across Dubai and Sharjah are structured around rental duration and driver requirement. Daily rental starts from AED 450 without a driver and AED 500 with a driver — suited for single-day frozen cargo runs or urgent same-day requirements. Weekly rates run from AED 1,800 to AED 2,500 depending on driver inclusion, covering short-term frozen distribution campaigns or seasonal demand periods. Monthly arrangements start from AED 6,500 and deliver the lowest per-day rate for businesses running fixed daily frozen cargo cycles across Dubai or Sharjah.",
    descriptionNote:
      <>All rates include GPS tracking, onboard temperature monitoring, and mandatory pre-cooling to -18°C as standard. Final pricing is confirmed based on your frozen cargo type, delivery route, and rental duration. UAE VAT applies where applicable. <Link to="/contact-us" className="internal-link">Contact our team</Link> directly for route-specific pricing across Dubai, Sharjah, and wider UAE destinations.</>,
  },

  {
    type: "fvfr-delivery-zones",
    title:
      "Freezer Van Operations Across Dubai & Sharjah — Frozen Cargo Delivery Zones",
    description:
      "Sub-zero frozen cargo delivery across Dubai and Sharjah operates under the same -18°C requirement regardless of emirate — which is why our freezer van fleet is coordinated across both locations under a single dispatch system rather than treated as two separate operations. Vehicle availability, pre-cooling scheduling, and route confirmation are managed from both branches simultaneously.",
    zones: [
      {
        dubai:
          "Deira & Bur Dubai — High-density urban zones with frequent multi-stop frozen cargo routes serving retail outlets, restaurants, and food service operators where door-opening frequency creates the highest sub-zero pressure per route",
        sharjah:
          "Sharjah Industrial Area — Primary frozen goods manufacturing and cold storage zone where freezer van deployments cover production-to-distribution movement across Sharjah's most active logistics corridor",
      },
      {
        dubai:
          "Al Quoz & Business Bay — Commercial and mixed-use zones handling frozen food distribution to hotels, catering operations, and retail chains across Dubai's central business districts",
        sharjah:
          "Al Sajaa Industrial — Freezer van routes covering frozen cargo originating from Sharjah's largest industrial cluster moving toward retail and distribution points across the emirate",
      },
      {
        dubai:
          "Jumeirah — Hospitality and residential supply routes where frozen meat, seafood, and packaged frozen goods move to hotels, restaurants, and supermarkets under strict delivery timing",
        sharjah:
          "Muwailih — Growing residential and commercial distribution zone requiring consistent frozen cargo delivery across expanding retail and food service networks",
      },
      {
        dubai:
          "Ras Al Khor — Primary Dubai dispatch base for freezer van operations, covering warehouse frozen cargo movement and same-day deployment across Dubai commercial zones",
        sharjah:
          "Sharjah City Centre — Urban frozen cargo distribution covering supermarkets, retail chains, and food service operators across Sharjah's main commercial corridors",
      },
    ],
  },

  {
    type: "fvfr-why-choose",
    title:
      "Why Businesses in Dubai & Sharjah Choose Our Freezer Van Fleet for Frozen Cargo Delivery",
    description:
      "Businesses across Dubai and Sharjah that depend on uninterrupted frozen cargo delivery return to operators whose fleet is genuinely built for sub-zero urban operations — not adapted from standard van configurations. Here is what separates our freezer van operation from standard rental providers:",
    reasons: [
      {
        title: "Toyota Hiace Modified for UAE Climate",
        description:
          "Our fleet runs on Toyota Hiace models modified specifically for extreme UAE heat exposure — not standard off-the-shelf freezer vans fitted with basic refrigeration units as an afterthought. Every modification is oriented around maintaining -18°C performance under the operating conditions that Dubai and Sharjah urban delivery actually creates.",
      },
      {
        title: "Same-Day Deployment From Both Branches",
        description:
          "Standby freezer vans are maintained at both our Ras Al Khor and Sharjah branches specifically for urgent frozen cargo requirements — available for same-day dispatch without pulling vehicles from scheduled operations.",
      },
      {
        title: "In-House Refrigeration Maintenance",
        description:
          "Every branch maintains its own mechanics for refrigeration system servicing. No third-party repair dependency means Thermo King and Thermal Master unit performance stays within operational standards without external service delays affecting fleet availability.",
      },
      {
        title: "Single Booking Across Both Emirates",
        description:
          "Businesses running frozen cargo routes across Dubai and Sharjah simultaneously are handled under one booking arrangement — coordinated from whichever branch sits closest to the loading point without separate contracts or split scheduling.",
      },
    ],
  },

  {
    type: "fvfr-faq",
    title: "Freezer Van Rental — Frequently Asked Questions",
    faqs: [
      {
        q: "What temperature do your freezer vans maintain during delivery?",
        a: "Our freezer vans maintain a consistent internal temperature of -18°C throughout every delivery route across Dubai and Sharjah. Temperature is confirmed through onboard digital logging monitored by both the driver and our dispatch team from departure through to final delivery point.",
      },
      {
        q: "Can a freezer van maintain -18°C through multiple door openings in Dubai summer heat?",
        a: "Yes — specifically because our fleet runs Thermo King and Thermal Master heavy-duty refrigeration systems engineered for rapid thermal recovery between stops. Standard freezer van units lose sub-zero recovery capacity under repeated door openings in UAE summer heat. Our systems are built to handle exactly that operating condition across full multi-stop urban routes.",
      },
      {
        q: "What is the difference between a chiller van and a freezer van?",
        a: <>Temperature range. <Link to="/chiller-van-for-rent" className="internal-link">Chiller vans</Link> maintain 0°C to 8°C for cargo that requires cooling without freezing — dairy, fresh produce, flowers, and pharmaceuticals. Freezer vans maintain 0°C to -18°C for cargo that must remain frozen throughout transit — meat, seafood, ice cream, and packaged frozen goods.</>,
      },
      {
        q: "What frozen cargo types are your freezer vans suitable for?",
        a: "Our freezer van fleet handles frozen meat, poultry, seafood, ice cream, frozen desserts, and packaged frozen goods across Dubai and Sharjah delivery routes where unbroken sub-zero conditions throughout transit are required.",
      },
      {
        q: "What is the minimum rental period for a freezer van?",
        a: "Daily rental is the minimum period available. Weekly and monthly arrangements are offered at progressively lower per-day rates for businesses running regular frozen cargo distribution cycles across Dubai or Sharjah.",
      },
      {
        q: "Do your freezer vans come with a driver?",
        a: "Both driver-included and self-drive options are available across Dubai and Sharjah. Self-drive requires a valid UAE driving license. Driver-included arrangements cover route coordination and loading supervision throughout the frozen cargo delivery.",
      },
    ],
  },

  {
    type: "cta",
    title:
      "Book a Freezer Van for Rent in Dubai or Sharjah for Your Next Frozen Cargo Delivery",
    paragraphs: [
      "Our freezer van fleet across Dubai and Sharjah is ready for your next frozen cargo booking. Call or WhatsApp our team directly with your sub-zero delivery requirement — vehicle availability and departure confirmation follows immediately.",
    ],
  },
];
